import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { isEmpty } from "../../../utils/Util";
import { GetDealerOnboardedLocationGroups } from "../../../utils/UtilTarget";
import { useLocation } from "react-router-dom";
import { all, HttpStatusCode } from "axios";
import {
  useHandleSurchargeForDealersMutation,
  useGetAllDealersWithSurchargeProfileQuery,
  useGetAllSurchargeProfilesQuery,
} from "../../../services/API/surchargeApiSlice";

import {
  SurchargeProfileResponse,
  ILocationGroups,
  DealerSurcharges,
  HandleDealerSurchargeRequest,
} from "../../../types/types";
import SurchargeDetail from "../Dealers/SurchargeDetail";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../common/components/ConfirmationModal";
import { JWTDecode } from "../../../utils/UtilTarget";

export default function ManageSurchargesForGroups() {
  const location = useLocation();
  const dealerData = location?.state?.DealerSummary;
  const [isDrawerOpen, setIsDrawerOpen] = useState<any>(false);
  const [surchargeProfileList, setSurchargeProfileList] = useState<
    SurchargeProfileResponse[]
  >([]);
  const [detailProfile, setDetailProfile] =
    useState<SurchargeProfileResponse | null>(null);
  const [profilesPayload, setProfilesPayload] = useState<DealerSurcharges[]>(
    [],
  );
  const [initialProfilesPayload, setInitialProfilesPayload] = useState<
    DealerSurcharges[]
  >([]);
  const [enabledSubmitButton, setEnabledSubmitButton] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(true);
  const [allonboardedLocationGroups, setAllonboardedLocationGroups] = useState<
    ILocationGroups[]
  >([]);
  const [isGroupDealer, setIsGroupDealer] = useState(false);
  const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
  const [applySurcharge] = useHandleSurchargeForDealersMutation();

  const { data: profileList = [], isLoading: isProfilesLoading } =
    useGetAllSurchargeProfilesQuery(null, {
      refetchOnMountOrArgChange: true,
    });

  const {
    data: allEnableSurchargeDealers = [],
    isLoading: isDealerProfilesLoading,
  } = useGetAllDealersWithSurchargeProfileQuery(null, {
    skip: !surchargeProfileList.length,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    document.title = "Setup Dealer Surcharges";

    if (isEmpty(dealerData)) return;

    const fetchLocationGroups = async () => {
      try {
        setIsProcessing(true);
        const groups = await GetDealerOnboardedLocationGroups(
          dealerData?.dmsDealerId,
          dealerData?.dealerId,
        );

        if (groups) {
          setAllonboardedLocationGroups(groups);
          setIsGroupDealer(true);
        } else {
          if (dealerData?.dealerOnboardingStatus == "Completed") {
            setAllonboardedLocationGroups([
              {
                companyDealerId: dealerData?.dealerId,
                name: dealerData?.dealerName,
                dmsDealerId: dealerData?.dmsDealerId,
                locationGroupId: 0,
                description: "",
              },
            ]);
          }
          setIsGroupDealer(false);
        }
      } finally {
        setIsProcessing(false);
      }
    };

    fetchLocationGroups();
  }, [dealerData]);

  useEffect(() => {
    if (profileList.length && dealerData?.countryCode) {
      const availableProfiles = profileList.filter(
        (x) => x.countryCode === dealerData.countryCode,
      );
      setSurchargeProfileList(availableProfiles);
      setIsProcessing(false);
    }
  }, [profileList, dealerData?.countryCode]);

  useEffect(() => {
    if (
      allonboardedLocationGroups.length &&
      surchargeProfileList.length &&
      allEnableSurchargeDealers.length
    ) {
      const initialPayload = allonboardedLocationGroups.map((row) => {
        const assignedProfile = allEnableSurchargeDealers.find(
          (p) => p.dealerId === row.companyDealerId,
        );

        return {
          dealerId: row.companyDealerId,
          profileId: assignedProfile?.profileId ?? EMPTY_GUID,
          enableSurcharge: !!assignedProfile?.profileId,
        };
      });

      setProfilesPayload(initialPayload);
      setInitialProfilesPayload(initialPayload);
    }
  }, [
    allonboardedLocationGroups,
    surchargeProfileList,
    allEnableSurchargeDealers,
  ]);

  useEffect(() => {
    const isChanged =
      JSON.stringify(profilesPayload) !==
      JSON.stringify(initialProfilesPayload);
    setEnabledSubmitButton(isChanged);
  }, [profilesPayload, initialProfilesPayload]);

  const handleProfileSelection = (
    profileId: string,
    companyDealerId: number,
  ) => {
    setProfilesPayload((prev) => {
      const index = prev.findIndex((p) => p.dealerId === companyDealerId);
      const updatedEntry: DealerSurcharges = {
        dealerId: companyDealerId,
        profileId: profileId ? profileId : EMPTY_GUID,
        enableSurcharge: !!profileId,
      };

      if (index !== -1) {
        const updated = [...prev];
        updated[index] = updatedEntry;
        return updated;
      }
      return [...prev, updatedEntry];
    });
  };

  const handleConfirmation = async () => {
    try {
      setIsProcessing(true);
      console.log("Submitting payload:", profilesPayload);
      let token = localStorage.getItem("access_token");

      const request: HandleDealerSurchargeRequest = {
        dealerSurcharges: profilesPayload,
        appliedBy: JWTDecode(token, "name"),
      };

      const response = await applySurcharge(request).unwrap();
      if (response !== undefined && response.length) {
        const findErrors = response.find((x) => !x.success);
        if (findErrors) {
          Object.entries(response).forEach(([field, item]) => {
            if (item.success)
              toast.success(`${item.data!.dealerId} - ${item.message}`);
            else toast.error(`${item.data!.dealerId} - ${item.message}`);
          });
        } else
          toast.success("Surcharge operations were completed successfully.");
      } else {
        toast.error("An unexpected error occurred.");
      }
    } catch (err) {
      toast.error("Failed to update surcharges");
    } finally {
      setIsProcessing(false);
    }
  };

  const isLoading = useMemo(
    () => isDealerProfilesLoading || isProfilesLoading || isProcessing,
    [isDealerProfilesLoading, isProfilesLoading, isProcessing],
  );

  const toggleDrawer = (profile: any) => {
    setDetailProfile(profile);
    setIsDrawerOpen(!isDrawerOpen);
  };

  return (
    <Box>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress size={25} />
        </Box>
      ) : (
        <Box>
          {/* Header Buttons */}
          <Box display="flex" justifyContent="flex-end" mb={1}>
            {isProcessing && (
              <CircularProgress
                size={25}
                sx={{ mr: 2, mt: 0.5 }}
                color="info"
              />
            )}
            <Button
              variant="contained"
              disabled={!enabledSubmitButton || isProcessing}
              color="primary"
              onClick={() => setModalOpen(true)}
            >
              Save
            </Button>
          </Box>
          {/* Data Table */}
          <Paper>
            <TableContainer sx={{ maxHeight: "30vh", overflow: "auto" }}>
              <Table
                size="small"
                className="sticky-header"
                sx={{ tableLayout: "fixed", width: "100%" }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Company Dealer Id</TableCell>
                    <TableCell>DMS Dealer Id</TableCell>
                    <TableCell>
                      {isGroupDealer ? "Group Name" : "Dealer Name"}
                    </TableCell>
                    <TableCell width={"30%"}>Surcharge Profile</TableCell>
                    <TableCell width={"15%"}>Profile</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allonboardedLocationGroups.length ? (
                    allonboardedLocationGroups.map((row) => {
                      const currentSelection = profilesPayload.find(
                        (p) => p.dealerId === row.companyDealerId,
                      );
                      const selectedProfile =
                        surchargeProfileList.find(
                          (p) => p.id === currentSelection?.profileId,
                        ) || null;

                      return (
                        <TableRow key={row.companyDealerId}>
                          <TableCell>{row.companyDealerId}</TableCell>
                          <TableCell>{row.dmsDealerId}</TableCell>
                          <TableCell
                            sx={{
                              whiteSpace: "normal",
                              wordWrap: "break-word",
                            }}
                          >
                            {row.name}
                          </TableCell>
                          <TableCell>
                            <Autocomplete
                              autoHighlight
                              fullWidth
                              //disabled={row.isDealerOnboarded}
                              options={surchargeProfileList}
                              value={selectedProfile}
                              getOptionLabel={(option) => option.name}
                              sx={{
                                "& .MuiAutocomplete-input, & .MuiAutocomplete-option":
                                  {
                                    fontSize: "14px",
                                  },
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  variant="standard"
                                  InputProps={{
                                    ...params.InputProps,
                                    sx: { fontSize: "14px" },
                                  }}
                                />
                              )}
                              onChange={(_, value) => {
                                handleProfileSelection(
                                  value ? value.id : "",
                                  row.companyDealerId,
                                );
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {selectedProfile && (
                              <button
                                onClick={() => toggleDrawer(selectedProfile)}
                              >
                                View details
                              </button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5}>
                        Onboarded dealer(s) not found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {detailProfile && (
            <SurchargeDetail
              open={isDrawerOpen}
              onClose={() => toggleDrawer(null)}
              profileDetails={detailProfile}
            />
          )}
          {/* Confirmation Modal */}
          <ConfirmationModal
            open={isModalOpen}
            onClose={() => setModalOpen(false)}
            heading="Confirm Surcharge Profile Updates"
            confirmationText="The selected surcharge profiles will be applied to all terminals for the respective dealers. If no profile is selected for a dealer, the surcharge will be disabled. Do you wish to proceed?"
            confirmationButtonText="Proceed"
            onConfirm={handleConfirmation}
          />
        </Box>
      )}
    </Box>
  );
}
